<template>
  <v-card width="100%" flat color="transparent">
    <v-card-text
      class="px-0"
      :class="mdWrap ? 'suggestions-container-wrap' : 'suggestions-container'"
    >
      <v-row class="flex-nowrap" :class="mdWrap ? 'flex-md-wrap' : ''" dense>
        <template v-for="(suggestion, index) in suggestions">
          <v-col :key="index" :cols="cols" :sm="sm" :md="md" :lg="lg">
            <v-hover v-slot="{ hover }">
              <v-card
                :elevation="hover ? 5 : 1"
                @click="onSuggestionClick(suggestion)"
              >
                <v-img
                  :src="suggestion.PictureUrl"
                  :aspect-ratio="1.618 / 1"
                  class="d-flex align-end"
                >
                  <!-- Discount lable -->
                  <div
                    v-if="globalLabel !== ''"
                    class="d-inline-flex pa-2 discount-label"
                  >
                    {{ globalLabel }}
                  </div>
                  <HighlightingLabel
                    v-else
                    :label="suggestion.HighlightingLabel"
                    :visible="suggestion.Highlighted"
                  />
                </v-img>
                <div
                  class="col-12 blue-grey--text text--darken-4 itemTitleFontSize text-center font-weight-bold text-truncate"
                >
                  {{ suggestion.Name }}
                </div>
                <div v-if="showInSale" style="min-height: 50px">
                  <v-col class="pt-0 pb-1">
                    <v-row class="ma-0" justify="end" align="baseline">
                      <div
                        class="discount-price text-body-2 mr-1"
                        v-if="globalDiscountPercentage != 0"
                      >
                        {{
                          formatter.format(
                            isWeightedProduct(suggestion)
                              ? getSuggestionOverviewPrice(suggestion)
                              : getSuggestionUnitPrice(suggestion)
                          )
                        }}
                      </div>
                      <div
                        class="discount-price text-body-2 mr-1"
                        v-else-if="isProductDiscounted(suggestion)"
                      >
                        {{
                          formatter.format(
                            isWeightedProduct(suggestion)
                              ? getSuggestionRetailOverviewPrice(suggestion)
                              : getSuggestionRetailUnitPrice(suggestion)
                          )
                        }}
                      </div>
                      <div>
                        <span
                          class="text-subtitle-1 font-weight-medium"
                          :class="
                            productGeneralDiscount(suggestion)
                              ? 'red--text'
                              : ''
                          "
                        >
                          {{ formatter.format(getProductPrice(suggestion)) }}
                        </span>
                        <span
                          v-if="isWeightedProduct(suggestion)"
                          class="text-caption"
                        >
                          /
                          <span v-if="suggestion.WeightedInfo.OverviewSize > 1">
                            {{ suggestion.WeightedInfo.OverviewSize }}
                          </span>
                          {{ suggestion.WeightedInfo.Unit }}
                        </span>
                      </div>
                      <div class="text-caption text--secondary ml-1">
                        {{ getSuggestionPriceSuffix() }}
                      </div>
                    </v-row>

                    <!-- Minimum quantity for Weighted product -->
                    <div
                      v-if="
                        isWeightedProduct(suggestion) &&
                        getDisplayMinQuantity(suggestion) >
                          getDisplayStepSize(suggestion)
                      "
                      class="text-caption text--primary text-right"
                    >
                      Minimum
                      {{ getDisplayMinQuantity(suggestion) }}
                      {{ getDisplayUnit(suggestion) }}
                    </div>
                  </v-col>
                </div>
              </v-card>
            </v-hover>
          </v-col>
        </template>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import uiHelpers from '@/shared/ui_helpers';
import utility from '@/shared/utility';
import doRequest, { addLog } from '@/api/dssRequest';
import {
  hasGeneralDiscount,
  isOnDiscount,
  personalDiscount,
  applyPersonalDiscount,
  isWeightedProduct,
  getUnitPrice,
  getRetailUnitPrice,
  getOverviewPrice,
  getRetailOverviewPrice,
  getDisplayMinQuantity,
  getDisplayUnit,
  getDisplayStepSize,
} from '@/objects/Product';
import HighlightingLabel from '@/components/generalComponents/HighlightingLabel';

export default {
  mixins: [uiHelpers],
  name: 'Suggestions',
  computed: {
    userObjectId() {
      return this.$store.getters['auth/userObjectId'];
    },
    isProfUser() {
      return this.$store.getters.isProfUser;
    },
  },
  props: {
    suggestions: {
      type: Array,
      required: true,
      default: () => [],
    },
    cols: {
      type: Number,
      default: 7,
    },
    sm: {
      type: Number,
      default: 5,
    },
    md: {
      type: Number,
      default: 3,
    },
    lg: {
      type: Number,
      default: 2,
    },
    mdWrap: {
      type: Boolean,
      default: true,
    },
    showInSale: {
      type: Boolean,
      default: false,
    },
    globalLabel: {
      type: String,
      default: '',
    },
    globalDiscountPercentage: {
      type: Number,
      default: 0,
    },
  },
  components: {
    HighlightingLabel,
  },
  data() {
    return {
      formatter: new Intl.NumberFormat('nl', {
        style: 'currency',
        currency: 'EUR',
      }),
    };
  },
  methods: {
    /**
     * Function to get the details of the selected category
     */
    getCategoryDetails(suggestion) {
      const payload = {
        ProductCategoryId: suggestion.ObjectId,
      };
      const object = this.userObjectId;
      doRequest(
        (response) => {
          const category = response.data;

          // Add user interaction to log
          addLog(
            this.userObjectId,
            suggestion.ObjectId,
            'ProductCategory',
            'Booking',
            -1
          );

          // route to eventDetails
          this.goToPage('ProductCategory', {
            event_id: category.ProductCategory.EventId,
            category_id: suggestion.ObjectId,
          });
        },
        () => {},
        'getProductsByCategory',
        object,
        payload,
        'GET'
      );
    },
    /**
     * call to get the product details for specific productId
     */
    getProductDetails(suggestion) {
      const payload = {
        IncludeAllPartnersInPartnerGroup: true,
        ProductIds: [suggestion.ObjectId],
      };
      const object = this.userObjectId;
      doRequest(
        (response) => {
          if (
            response.data.Products == null ||
            response.data.Products.length === 0
          ) {
            return;
          }

          const product = response.data.Products[0];

          // Add user interaction to log
          addLog(
            this.userObjectId,
            product.ProductId,
            'Product',
            'Booking',
            -1
          );

          // always go to the first category of the product
          const firstElement = 0;
          // route to the details of the product
          this.goToPage('ProductWithCategory', {
            event_id: product.EventId,
            category_id: product.Categories[firstElement].Id,
            product_id: product.Id,
          });
        },
        () => {},
        'getProducts',
        object,
        payload,
        'GET'
      );
    },
    /**
     * function that checks the type of the suggestion and route to the next page
     * @param suggestion
     */
    onSuggestionClick(suggestion) {
      switch (suggestion.ObjectType) {
        case 'Event':
          // Add user interaction to log
          addLog(this.userObjectId, suggestion.ObjectId, 'Event', 'Home', -1);

          // route to event page
          this.goToPage('EventDetails', {
            event_id: suggestion.ObjectId,
          });
          break;
        case 'Product':
          // get the product eventId and route
          this.getProductDetails(suggestion);
          break;
        case 'ProductCategory':
          // get the category eventId and route
          this.getCategoryDetails(suggestion);
          break;
        default:
          // else don't do anything
          break;
      }

      this.$emit('click', suggestion);
    },
    // getSuggestionPrice(suggestion) {
    //   if (this.isProfUser) {
    //     return suggestion.PriceExclVat;
    //   }
    //   return suggestion.Price;
    // },
    // getSuggestionRetailPrice(suggestion) {
    //   if (this.isProfUser) {
    //     return utility.getPriceExclVAT(suggestion.RetailPrice, suggestion.VatPercentage);
    //   }
    //   return suggestion.RetailPrice;
    // },
    getSuggestionRetailUnitPrice(product) {
      if (this.productGeneralDiscount(product)) {
        return getRetailUnitPrice(product);
      }
      return getUnitPrice(product);
    },
    getSuggestionUnitPrice(product) {
      return this.getPriceForPersonalDiscount(getUnitPrice(product), product);
    },
    getSuggestionRetailOverviewPrice(product) {
      if (this.productGeneralDiscount(product)) {
        return getRetailOverviewPrice(product);
      }
      return getOverviewPrice(product);
    },
    getSuggestionOverviewPrice(product) {
      return this.getPriceForPersonalDiscount(
        getOverviewPrice(product),
        product
      );
    },
    getPriceForPersonalDiscount(originalPrice, product) {
      let price = originalPrice;
      if (applyPersonalDiscount(product)) {
        price = utility.getDiscountPrice(
          originalPrice,
          personalDiscount(product)
        );
      }
      return price;
    },
    getSuggestionPriceSuffix() {
      if (this.isProfUser) {
        return this.$translate('EXCL_VAT');
      }
      return '';
    },
    isProductDiscounted(product) {
      return isOnDiscount(product) || this.globalDiscountPercentage != 0;
    },
    productGeneralDiscount(product) {
      return hasGeneralDiscount(product) || this.globalDiscountPercentage != 0;
    },
    getProductPrice(suggestion) {
      const price = isWeightedProduct(suggestion)
        ? this.getSuggestionOverviewPrice(suggestion)
        : this.getSuggestionUnitPrice(suggestion);
      return (price / 100) * (100 - this.globalDiscountPercentage);
    },
    isWeightedProduct,
    getDisplayMinQuantity,
    getDisplayUnit,
    getDisplayStepSize,
  },
};
</script>

<style scoped lang="scss">
.suggestions-container {
  overflow-x: auto;
}
.suggestions-container-wrap {
  overflow-x: auto;

  @media screen and (min-width: 960px) {
    overflow-x: hidden;
  }
}

.discount-label {
  color: white;
  font-weight: bold;
  font-size: 16px;
  background: red;
  border-radius: 0 0 15px 0;
  position: absolute;
  top: 0px;
  left: 0px;
}
</style>
